import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Download, FileUp, Trash2, Truck } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, errMsg, fmtNum, ts, x12Date } from "@/lib/api";
import { PageHeader, Panel, Th, Td, Empty, btn, field } from "@/components/ui-kit";

const WEEKS = Array.from({ length: 13 }, (_, i) => `W${i + 1}`);

const template = () => {
  const csv = ["Part,Site," + WEEKS.join(","), "PAC60066,XD1," + WEEKS.map(() => 0).join(","), "PAC60066,XD2," + WEEKS.map(() => 0).join(",")].join("\n");
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
  a.download = "forecast_template_13_weeks.csv";
  a.click();
};

const Upload = ({ onDone }) => {
  const [file, setFile] = useState(null);
  const [start, setStart] = useState("");
  const [res, setRes] = useState(null);
  const send = async () => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("week1_start", start);
    try {
      const r = await api.post("/forecasts/upload", fd);
      setRes(r.data);
      r.data.errors.length ? toast.warning(`Imported ${r.data.imported} rows, ${r.data.errors.length} skipped`) : toast.success(`Imported ${r.data.imported} forecast rows`);
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Panel title="Upload 13-week forecast" testId="forecast-upload-panel">
      <div className="flex flex-wrap items-end gap-3 p-5">
        <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-sm border border-slate-700 px-3 py-2 text-xs text-slate-200 hover:bg-slate-800">
          <FileUp className="h-3.5 w-3.5" /> {file ? file.name : "Choose CSV or Excel"}
          <input type="file" accept=".csv,.xlsx" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="forecast-file-input" />
        </label>
        <label className="block">
          <span className="mb-1 block font-mono text-[10px] uppercase tracking-wider text-slate-500">Week 1 starts</span>
          <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className={`h-9 ${field}`} data-testid="forecast-week1-input" />
        </label>
        <Button onClick={send} disabled={!file} className={btn.primary} data-testid="forecast-upload-btn">Upload forecast</Button>
        <Button variant="ghost" onClick={template} className={btn.ghost} data-testid="forecast-template-btn"><Download className="mr-1.5 h-4 w-4" /> Template</Button>
      </div>
      <p className="px-5 pb-4 text-xs text-slate-500">One row per part and xDock site: Part, Site (XD1/XD2/XD3), then W1 to W13 quantities. A new upload replaces that part and site's previous forecast.</p>
      {res?.errors?.length > 0 && (
        <div className="mx-5 mb-5 rounded-sm border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-200" data-testid="forecast-upload-errors">
          {res.errors.map((e) => <div key={e}>{e}</div>)}
        </div>
      )}
    </Panel>
  );
};

const ReleaseDialog = ({ s, onClose, onDone }) => {
  const nav = useNavigate();
  const [qty, setQty] = useState({});
  const [date, setDate] = useState("");
  useEffect(() => {
    if (s) setQty(Object.fromEntries(s.lines.filter((l) => l.suggested_qty > 0).map((l) => [l.part_no, l.suggested_qty])));
  }, [s]);
  const release = async () => {
    try {
      const r = await api.post("/planning/release", { site_code: s.site_code, ship_date: date, lines: Object.entries(qty).map(([part_no, q]) => ({ part_no, qty: Number(q) || 0 })) });
      toast.success(`Replenishment ${r.data.po_number} created`);
      onClose();
      onDone();
      nav(`/orders/${r.data.id}`);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open={!!s} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg border-slate-800 bg-[#1F232C] text-slate-100" data-testid="release-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Release truckload to {s?.site_code}</DialogTitle>
          <DialogDescription className="text-slate-400">Creates a replenishment shipment in Received status. The processor then ships it to the xDock as usual, and Group O's 861 Receiving Advice closes it.</DialogDescription>
        </DialogHeader>
        {Object.keys(qty).map((p) => (
          <div key={p} className="flex items-center justify-between gap-3">
            <span className="font-mono text-sm text-slate-100">{p}</span>
            <Input type="number" value={qty[p]} onChange={(e) => setQty({ ...qty, [p]: e.target.value })} className={`w-36 ${field}`} data-testid={`release-qty-${p}`} />
          </div>
        ))}
        <label className="block">
          <span className="mb-1 block font-mono text-[10px] uppercase tracking-wider text-slate-500">Planned ship date</span>
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={field} data-testid="release-ship-date" />
        </label>
        <Button onClick={release} className={btn.primary} data-testid="release-confirm-btn"><Truck className="mr-1.5 h-4 w-4" /> Create replenishment shipment</Button>
      </DialogContent>
    </Dialog>
  );
};

const SiteCard = ({ s, onRelease }) => (
  <Panel testId={`plan-site-${s.site_code}`}>
    <div className="flex items-center justify-between border-b border-slate-800 px-5 py-4">
      <div>
        <div className="font-heading text-lg font-semibold text-slate-100">{s.site_code}</div>
        <div className="font-mono text-[10px] uppercase tracking-wider text-slate-500">{fmtNum(s.pallets)} pallets suggested</div>
      </div>
      <div className="text-right">
        <div className={`flex items-center gap-2 font-heading text-3xl font-semibold ${s.trucks ? "text-amber-300" : "text-green-300"}`} data-testid={`plan-trucks-${s.site_code}`}>
          <Truck className="h-6 w-6" /> {s.trucks}
        </div>
        <div className="font-mono text-[10px] uppercase tracking-wider text-slate-500">{s.trucks ? `trucks · last ${s.fill_pct}% full` : "no truck needed"}</div>
        {s.lines.some((l) => l.suggested_qty > 0) && (
          <Button size="sm" onClick={() => onRelease(s)} className={`mt-2 ${btn.primary}`} data-testid={`plan-release-${s.site_code}`}>Release truckload</Button>
        )}
      </div>
    </div>
    {s.lines.length ? (
      <table className="w-full">
        <thead><tr><Th>Part</Th><Th className="text-right">On hand</Th><Th className="text-right">Transit</Th><Th className="text-right">Planned</Th><Th className="text-right">Cover</Th><Th className="text-right">Suggest</Th></tr></thead>
        <tbody className="divide-y divide-slate-800/70">
          {s.lines.map((l) => (
            <tr key={l.part_no} data-testid={`plan-line-${s.site_code}-${l.part_no}`}>
              <Td className="font-mono text-slate-100">{l.part_no}</Td>
              <Td className="text-right font-mono">{fmtNum(l.on_hand)}</Td>
              <Td className="text-right font-mono text-sky-300">{fmtNum(l.in_transit)}</Td>
              <Td className="text-right font-mono text-violet-300" data-testid={`plan-planned-${s.site_code}-${l.part_no}`}>{fmtNum(l.planned)}</Td>
              <Td className="text-right font-mono">{l.days_cover}d</Td>
              <Td className="text-right font-mono text-amber-300">
                {l.suggested_qty ? fmtNum(l.suggested_qty) : "—"}
                {l.pallets ? <div className="text-[10px] text-slate-500">{l.pallets} pallets</div> : null}
                {l.pallet_qty_missing && l.suggested_qty ? <div className="text-[10px] text-rose-300">set units/pallet</div> : null}
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    ) : <Empty>No forecast for this site.</Empty>}
  </Panel>
);

export default function Planning() {
  const [plan, setPlan] = useState(null);
  const [rows, setRows] = useState([]);
  const [rel, setRel] = useState(null);
  const load = () => {
    api.get("/planning").then((r) => setPlan(r.data));
    api.get("/forecasts").then((r) => setRows(r.data));
  };
  useEffect(load, []);
  const del = async (id) => {
    await api.delete(`/forecasts/${id}`);
    load();
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Forecast & truckload planning"
        subtitle={plan ? `Suggestions keep each xDock at ${plan.threshold_days} days minimum cover${plan.peak ? " (peak)" : ""} plus ${plan.lead_days} days until the truck arrives = ${plan.target_days} days. Full pallets, at least each item's MOQ; trucks use each item's pallets per FTL (default ${plan.pallets_per_truck}, change in Partner Settings).` : ""}
      />
      <Upload onDone={load} />
      <div className="grid gap-6 xl:grid-cols-3">{plan?.sites.map((s) => <SiteCard key={s.site_code} s={s} onRelease={setRel} />)}</div>
      <ReleaseDialog s={rel} onClose={() => setRel(null)} onDone={load} />
      <Panel title="Current forecast (units per week)" testId="forecast-table-panel">
        {rows.length ? (
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="forecast-table">
              <thead className="border-b border-slate-800"><tr><Th>Site</Th><Th>Part</Th>{WEEKS.map((w) => <Th key={w} className="text-right">{w}</Th>)}<Th>Uploaded</Th><Th></Th></tr></thead>
              <tbody className="divide-y divide-slate-800/70">
                {rows.map((f) => (
                  <tr key={f.id} data-testid={`forecast-row-${f.site_code}-${f.part_no}`}>
                    <Td className="font-mono text-emerald-300">{f.site_code}</Td>
                    <Td className="font-mono text-slate-100">{f.part_no}</Td>
                    {f.weeks.map((q, i) => <Td key={i} className="text-right font-mono">{fmtNum(q)}</Td>)}
                    <Td className="whitespace-nowrap text-slate-500">{ts(f.uploaded_at)} · {f.uploaded_by}{f.week1_start ? ` · W1 ${x12Date(f.week1_start)}` : ""}</Td>
                    <Td><Button size="sm" variant="ghost" onClick={() => del(f.id)} className={btn.danger} data-testid={`forecast-delete-${f.site_code}-${f.part_no}`}><Trash2 className="h-3.5 w-3.5" /></Button></Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <Empty testId="forecast-empty">No forecast uploaded yet. Download the template, fill in W1 to W13 per part and site, then upload.</Empty>}
      </Panel>
    </div>
  );
}
